export interface DirectTransport {
  send(data: string): void;
  onMessage(handler: (data: string) => void): void;
  close(): void;
  refreshRegistration(): void;
}

export interface DirectTransportOptions {
  getRegistrationPayload?: () => Record<string, unknown> | null;
  onStatus?: (event: { connected: boolean }) => void;
  onFlow?: (event: { executionId: string; status: string }) => void;
  debug?: boolean;
  maxQueueSize?: number;
  reconnectBaseDelayMs?: number;
  reconnectMaxDelayMs?: number;
}

const DEFAULT_MAX_QUEUE = 100;
const DEFAULT_BASE_DELAY = 1000;
const DEFAULT_MAX_DELAY = 30000;

// Direct WebSocket transport for the headless embed: same interface the browser-client
// expects from BotWidgetManager.createPostMessageTransport, but backed by a WebSocket
// straight to the backend instead of the player-iframe postMessage bridge. Browser-client
// frames travel wrapped in the existing wire protocol ({ action: 'browserMessage',
// payload: { data } } outbound; { action: 'message', payload: { browserMessage } }
// inbound), so the backend sees the exact envelope the widget path already produces.
export const createDirectWebSocketTransport = (
  wsUrl: string,
  options: DirectTransportOptions = {}
): DirectTransport => {
  const maxQueue = options.maxQueueSize ?? DEFAULT_MAX_QUEUE;
  const baseDelay = options.reconnectBaseDelayMs ?? DEFAULT_BASE_DELAY;
  const maxDelay = options.reconnectMaxDelayMs ?? DEFAULT_MAX_DELAY;

  let ws: WebSocket | null = null;
  let messageHandler: ((data: string) => void) | null = null;
  let closed = false;
  let reconnectAttempts = 0;
  let reconnectTimer: number | null = null;
  let lastConnected = false;
  const queue: string[] = [];

  const log = (...args: unknown[]) => {
    if (options.debug) console.log('[Getdemo][headless]', ...args);
  };

  const emitStatus = (connected: boolean) => {
    if (connected === lastConnected) return;
    lastConnected = connected;
    options.onStatus?.({ connected });
  };

  const rawSend = (frame: string): boolean => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(frame);
      return true;
    }
    return false;
  };

  const sendRegistration = () => {
    const payload = options.getRegistrationPayload?.();
    if (!payload) return;
    const sent = rawSend(JSON.stringify({ action: 'registerExecutor', payload }));
    if (sent) log('registerExecutor sent', payload);
  };

  const flushQueue = () => {
    while (queue.length > 0 && ws && ws.readyState === WebSocket.OPEN) {
      const frame = queue.shift();
      if (frame) ws.send(frame);
    }
  };

  const scheduleReconnect = () => {
    if (closed || reconnectTimer !== null) return;
    const delay = Math.min(baseDelay * Math.pow(2, reconnectAttempts), maxDelay);
    const jittered = delay / 2 + Math.random() * (delay / 2);
    reconnectAttempts += 1;
    log(`reconnecting in ~${Math.round(jittered)}ms (attempt ${reconnectAttempts})`);
    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, jittered);
  };

  const deliver = (data: unknown) => {
    if (!messageHandler || data === undefined || data === null) return;
    messageHandler(typeof data === 'string' ? data : JSON.stringify(data));
  };

  const handleFrame = (raw: string) => {
    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (!parsed || typeof parsed !== 'object') return;
    const { action, payload } = parsed;
    if (action === 'flowStatus') {
      const executionId = payload?.executionId;
      const status = payload?.status;
      if (executionId && status) options.onFlow?.({ executionId, status });
      return;
    }
    if (action === 'message' && payload && payload.browserMessage !== undefined) {
      deliver(payload.browserMessage);
      return;
    }
    if (!action && typeof parsed.type === 'string') {
      deliver(raw);
    }
  };

  const connect = () => {
    if (closed) return;
    try {
      ws = new WebSocket(wsUrl);
    } catch (error) {
      log('WebSocket construction failed', error);
      scheduleReconnect();
      return;
    }
    ws.onopen = () => {
      reconnectAttempts = 0;
      log('connected');
      sendRegistration();
      flushQueue();
      emitStatus(true);
    };
    ws.onmessage = event => {
      if (typeof event.data === 'string') handleFrame(event.data);
    };
    ws.onclose = () => {
      ws = null;
      emitStatus(false);
      if (!closed) scheduleReconnect();
    };
    ws.onerror = () => {
      log('websocket error');
    };
  };

  connect();

  return {
    send: (data: string) => {
      const frame = JSON.stringify({ action: 'browserMessage', payload: { data } });
      if (rawSend(frame)) return;
      if (queue.length >= maxQueue) queue.shift();
      queue.push(frame);
    },
    onMessage: (handler: (data: string) => void) => {
      messageHandler = handler;
    },
    close: () => {
      closed = true;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      messageHandler = null;
      if (ws) {
        try {
          ws.close();
        } catch {
          /* already closed */
        }
        ws = null;
      }
      emitStatus(false);
    },
    refreshRegistration: () => {
      sendRegistration();
    }
  };
};

export default createDirectWebSocketTransport;
